import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password required'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Login failed'); setLoading(false); return; }
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      toast.success(`Welcome, ${data.user.name}`);
      onLogin(data.user, data.token);
    } catch {
      setError('Connection error');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { setError('Email required'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Request failed'); setLoading(false); return; }
      setForgotSent(true);
    } catch {
      setError('Connection error');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0f2340 50%, #0a1628 100%)' }}>
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/images/kiel-logo.png" alt="Kiel Americas" className="h-16 object-contain drop-shadow-lg" data-testid="login-logo" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="login-title">
            Production Management
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {mode === 'login' ? 'Sign in to continue' : 'Reset your password'}
          </p>
        </div>

        <div className="bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-xl p-6 shadow-2xl">
          {mode === 'login' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-3 py-2.5 rounded-lg" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@company.com"
                    className="w-full bg-slate-900/50 border border-slate-600/50 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                    data-testid="login-email" autoFocus />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password"
                    className="w-full bg-slate-900/50 border border-slate-600/50 rounded-lg pl-10 pr-10 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                    data-testid="login-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 text-sm"
                data-testid="login-submit">
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <button type="button" onClick={() => { setMode('forgot'); setError(''); setForgotSent(false); setForgotEmail(email); }}
                className="w-full text-xs text-slate-500 hover:text-blue-400 transition-colors mt-2" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              {forgotSent ? (
                <div className="text-center space-y-3 py-2">
                  <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto" />
                  <p className="text-sm text-emerald-400 font-semibold">Request Submitted</p>
                  <p className="text-xs text-slate-400">Your password reset request has been sent to the system administrator. They will set a new password for you.</p>
                  <button onClick={() => { setMode('login'); setForgotSent(false); setError(''); }}
                    className="flex items-center gap-1 mx-auto text-xs text-blue-400 hover:text-blue-300 mt-4">
                    <ArrowLeft className="w-3 h-3" /> Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-3 py-2.5 rounded-lg">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  <p className="text-xs text-slate-400">Enter your email address. A reset request will be sent to the administrator for approval.</p>
                  <div>
                    <label className="text-xs font-medium text-slate-400 block mb-1.5">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="email@company.com"
                        className="w-full bg-slate-900/50 border border-slate-600/50 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                        data-testid="forgot-email" autoFocus />
                    </div>
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 text-sm">
                    {loading ? 'Submitting...' : 'Submit Reset Request'}
                  </button>
                  <button type="button" onClick={() => { setMode('login'); setError(''); }}
                    className="flex items-center gap-1 mx-auto text-xs text-slate-500 hover:text-blue-400 transition-colors">
                    <ArrowLeft className="w-3 h-3" /> Back to Sign In
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-600 mt-6">
          Kiel Americas - Production Management System v2.0
        </p>
      </div>
    </div>
  );
}
